import { dueAsStored } from '@/domain/due';
import {
  dateFace,
  faceNamesYear,
  parseTimeValue,
  type DateFace,
  type TimeValue,
} from '@/domain/time-value';
import type { Translator } from '@/i18n';

/**
 * A date value's TEXT (docs/DESIGN_SYSTEM.md "Values and entries": a calendar
 * sign and the date in tabular figures). Pure: the component reads the clock
 * and the browser's language and hands them in.
 *
 * Grain-honest through the domain's one reading of a time value
 * (`dateFace`, src/domain/time-value.ts, which the due label shares): a day
 * names its date, a week its ISO week, a month its month, a quarter its
 * quarter, a year itself. The year shows only when it is not the current one
 * (`faceNamesYear`). Days and months format in UTC, as the time model is
 * timezone-agnostic for them; a minute-grain value is an instant and formats
 * in the viewer's own zone.
 */

/** The clock-free face: the canonical value(s) as stored, cut to the day, for
 *  a render that cannot know the viewer's locale yet (the server and the first
 *  client paint, as `DateOnly` does). Never a label. */
export function dateAsStored(value: TimeValue | string): string {
  const canonical = typeof value === 'string' ? value : value.canonical;
  return canonical.split('/').map(dueAsStored).join('/');
}

/**
 * The date's text in `tag` (the BCP 47 tag `dateLocaleFor` picks) at `now`
 * (epoch ms). A range names both ends; an unreadable value reads as stored,
 * never a crash.
 */
export function dateText(
  t: Translator,
  tag: string,
  value: TimeValue | string,
  now: number,
): string {
  let tv: TimeValue;
  if (typeof value === 'string') {
    const parsed = parseTimeValue(value);
    if (!parsed.ok) return value;
    tv = parsed.value;
  } else {
    tv = value;
  }
  const end = faceText(t, tag, dateFace(tv, 'end'), now);
  if (!tv.canonical.includes('/')) return end;
  return t('values.date.range', { from: faceText(t, tag, dateFace(tv, 'start'), now), to: end });
}

function faceText(t: Translator, tag: string, face: DateFace, now: number): string {
  const year: Intl.DateTimeFormatOptions = faceNamesYear(face, now) ? { year: 'numeric' } : {};
  switch (face.grain) {
    case 'day':
      return new Date(face.ms).toLocaleDateString(tag, {
        day: 'numeric',
        month: 'short',
        ...year,
        timeZone: 'UTC',
      });
    case 'minute':
      return new Date(face.ms).toLocaleString(tag, {
        day: 'numeric',
        month: 'short',
        ...year,
        hour: '2-digit',
        minute: '2-digit',
      });
    case 'week': {
      const week = String(face.week).padStart(2, '0');
      return faceNamesYear(face, now)
        ? t('values.date.weekOfYear', { week, year: String(face.year) })
        : t('values.date.week', { week });
    }
    case 'month':
      return new Date(face.ms).toLocaleDateString(tag, {
        month: 'short',
        ...year,
        timeZone: 'UTC',
      });
    case 'quarter': {
      const quarter = String(face.quarter);
      return faceNamesYear(face, now)
        ? t('values.date.quarterOfYear', { quarter, year: String(face.year) })
        : t('values.date.quarter', { quarter });
    }
    case 'year':
      return String(face.year);
  }
}
